/**
 * Shared anonymous Firebase auth for public pages (CareStart /carestart3, Gemini Live, etc.).
 *
 * WHY this exists: callers used to read `auth.currentUser` synchronously and, finding it
 * null during Firebase's asynchronous session restore, immediately called
 * `signInAnonymously()`. Because `currentUser` is null for the first moment after load,
 * that lost the race on essentially every visit/interaction and minted a brand-new
 * anonymous user each time. The repeated sign-ins intermittently trip
 * `auth/too-many-requests` (and are fragile to transient `network-request-failed`), which
 * surfaced to users as a misleading "Unable to connect. Please check your internet
 * connection" screen.
 *
 * This helper instead waits for the first `onAuthStateChanged` emission (so a restored or
 * real user is reused), and only signs in anonymously when there is genuinely no user —
 * retrying transient failures with backoff.
 */

import firebaseApp from "services/firebase";

/** Firebase returns these when Anonymous sign-in is off or disallowed for the project. */
const ANONYMOUS_AUTH_BLOCKED_CODES = new Set([
  "auth/admin-restricted-operation",
  "auth/operation-not-allowed",
]);

/** Codes worth retrying — transient connectivity / rate-limit pressure rather than config. */
const TRANSIENT_AUTH_CODES = new Set([
  "auth/network-request-failed",
  "auth/too-many-requests",
  "auth/internal-error",
]);

/**
 * ⚠️ RUNNING OUT OF TIME IS NOT "NOBODY IS SIGNED IN". This used to be 2.5s, and on
 * expiry the caller went on to signInAnonymously(). Safari can take longer than that to
 * restore a session from IndexedDB, and an anonymous sign-in REPLACES whoever was about
 * to be restored: a patient who had just created an account from the discharge page and
 * came back from the verification email was silently swapped for a fresh guest, who
 * then got bounced to the sign-in page. The listener's first emission is the only
 * trustworthy "nobody here" answer, so on timeout we now give up with an error (the
 * pages offer "Try again") instead of guessing. A storage layer that never answers
 * would hang signInAnonymously() too, so nothing is lost by refusing.
 */
const AUTH_RESTORE_TIMEOUT_MS = 15000;

const restoreTimeoutError = () => {
  const err = new Error("Could not load your sign-in. Please reload the page and try again.");
  err.code = "auth/restore-timeout";
  return err;
};
const MAX_SIGN_IN_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 400;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function enhanceAnonSignInError(err, blockedHint, errorName) {
  const code = err && err.code;
  if (!code || !ANONYMOUS_AUTH_BLOCKED_CODES.has(code)) return err;
  const wrapped = new Error(`${blockedHint} (${code})`);
  wrapped.name = errorName;
  wrapped.code = code;
  wrapped.cause = err;
  return wrapped;
}

/**
 * Resolve to the current Firebase user once auth-state restoration settles.
 * `auth.currentUser` is synchronously null until the persisted session is restored, so we
 * wait for the first onAuthStateChanged tick. Rejects on timeout rather than resolving
 * null, because null sends the caller on to an anonymous sign-in (see above).
 * @returns {Promise<object | null>}
 */
function waitForInitialAuthUser(auth) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (user, err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        unsubscribe();
      } catch {
        /* listener may already be torn down */
      }
      if (err) reject(err);
      else resolve(user || null);
    };
    const timer = setTimeout(() => {
      if (auth.currentUser) finish(auth.currentUser);
      else finish(null, restoreTimeoutError());
    }, AUTH_RESTORE_TIMEOUT_MS);
    try {
      const maybeUnsub = auth.onAuthStateChanged(
        (user) => finish(user),
        () => finish(auth.currentUser),
      );
      if (typeof maybeUnsub === "function") unsubscribe = maybeUnsub;
    } catch {
      finish(auth.currentUser);
    }
  });
}

/**
 * The restored session, or null when nobody is signed in, and never an anonymous
 * sign-in of its own. For pages that must find out who is here without becoming a
 * guest by asking (the discharge page coming back from its verification email).
 * @returns {Promise<object | null>}
 */
export function getRestoredFirebaseUser() {
  if (typeof window === "undefined") return Promise.resolve(null);
  return waitForInitialAuthUser(firebaseApp.auth());
}

/**
 * Ensure a Firebase user (restored, real, or freshly anonymous) exists before any action
 * that requires an authenticated caller.
 * @param {{ logPrefix?: string, errorName?: string, blockedHint?: string }} [options]
 * @returns {Promise<object | null>}
 */
export async function ensureAnonymousFirebaseUser(options = {}) {
  if (typeof window === "undefined") return null;
  const {
    logPrefix = "[auth]",
    errorName = "AnonymousAuthError",
    blockedHint =
      "Enable Anonymous sign-in: Firebase Console → Authentication → Sign-in method → " +
      "Anonymous → Enable, then reload.",
  } = options;

  const auth = firebaseApp.auth();

  const restored = await waitForInitialAuthUser(auth);
  if (restored) return restored;

  let lastErr;
  for (let attempt = 1; attempt <= MAX_SIGN_IN_ATTEMPTS; attempt += 1) {
    // A user may have appeared between attempts (e.g. a parallel caller signed in).
    if (auth.currentUser) return auth.currentUser;
    try {
      const cred = await auth.signInAnonymously();
      return cred.user;
    } catch (e) {
      lastErr = e;
      const code = e && e.code;
      const isLastAttempt = attempt === MAX_SIGN_IN_ATTEMPTS;
      if (!code || !TRANSIENT_AUTH_CODES.has(code) || isLastAttempt) break;
      console.warn(
        `${logPrefix} signInAnonymously transient failure (${code}); ` +
          `retrying ${attempt}/${MAX_SIGN_IN_ATTEMPTS - 1}`,
      );
      await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
  }

  const mapped = enhanceAnonSignInError(lastErr, blockedHint, errorName);
  console.error(`${logPrefix} signInAnonymously failed`, mapped.cause || mapped);
  throw mapped;
}
